import { useMemo } from "react";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { getSFDCData } from "@/entities/Deal/Deal.queries";
import { longFormMoney, formatDate } from "@/utils/misc/format";
import { DetailTableRow } from "../../components/DetailTable";
import {
    USE_OF_FUNDS,
    UseOfFunds,
} from "@/entities/CapitalApplication/CapitalApplication.constants";
import {
    FinanceLabField,
    Frequency,
    FrequencyLabels,
    getFinanceLabMetrics,
    ResampleTransform,
} from "@/entities/FinanceLab";
import { COMING_SOON } from "@/entities/CreditBox/CreditBox.types";
import { useSetAtom } from "jotai";
import { useFlags } from "@/utils/flagsmith/useFlags";
import { getLastValue, getValueForDate } from "@/utils/metrics/metricsUtils";
import { AIMContextViewAtom } from "../../panels/AIMPanelContext";
import { AIMViewType } from "../../panels/AIMPanelContext";
import { DataSource } from "../../components/SourceTag";
import { MetricSource } from "@/features/Metrics/Metrics.types";
import { useCompany } from "@/apps/CompanyApp/hooks/useCompany";

interface UseDealStructureDataProps {
    opportunityId?: string;
}

export function useDealStructureData({
    opportunityId,
}: UseDealStructureDataProps = {}) {
    const flags = useFlags();
    const showComingSoon = flags.enabled("show-coming-soon-placeholders");
    const showPreAlphaFields = flags.enabled("show-pre-alpha-fields");
    const setAIMContextView = useSetAtom(AIMContextViewAtom);

    const sfdcData = useApiQuery(getSFDCData);
    const data = opportunityId
        ? sfdcData.data?.find((data) => data.id === opportunityId)
        : sfdcData.data?.[0];

    const currentYearMonth = useMemo(() => new Date().toISOString(), []);
    const previousYearMonth = useMemo(() => {
        const date = new Date();
        date.setFullYear(date.getFullYear() - 1);
        return date.toISOString();
    }, []);
    const { company } = useCompany();

    const { data: metrics, isLoading: isLoadingMetrics } = useApiQuery(
        getFinanceLabMetrics,
        {
            params: {
                labels: [
                    FinanceLabField.DEAL_CAPITAL_PRODUCT,
                    FinanceLabField.DEAL_INITIAL_DISBURSEMENT,
                    FinanceLabField.DEAL_TERM,
                    FinanceLabField.DEAL_CURRENT_PAY_P,
                    FinanceLabField.DEAL_CURRENT_PAY_CASH_P,
                    FinanceLabField.DEAL_CURRENT_PAY_TYPE,
                    FinanceLabField.DEAL_BROKER_FEE,
                    FinanceLabField.DEAL_MOIC,
                    FinanceLabField.DEAL_IRR,
                    FinanceLabField.DEAL_MONITORING_FEE,
                    FinanceLabField.DEAL_PREPAYMENT_PENALTY,
                    FinanceLabField.DEAL_LEGAL_FEES,
                    FinanceLabField.DEAL_RECURRING_PAYMENT,
                    FinanceLabField.DEAL_REPAYMENT_FREQUENCY,
                    FinanceLabField.DEAL_SECURITY_PACKAGE,
                    FinanceLabField.DEAL_FUNDED_DATED,
                    FinanceLabField.DEAL_USE_OF_PROCEEDS,
                    FinanceLabField.DEAL_OPERATIONAL_CONTROLS,
                    FinanceLabField.DEAL_CLOSE_DATE,
                    FinanceLabField.DEAL_COVENANTS_SUMMARY,
                ],
                from: previousYearMonth,
                to: currentYearMonth,
            },
        },
    );

    const { data: mfiMetrics, isLoading: isLoadingMfiMetrics } = useApiQuery(
        getFinanceLabMetrics,
        {
            params: {
                labels: [
                    FinanceLabField.DEAL_COLLECTIONS,
                    FinanceLabField.DEAL_DISBURSEMENTS,
                ],
                resampleTransform: ResampleTransform.MONTHLY_FROM_INCEPTION,
            },
        },
    );

    const handleMetricClick = (
        financeLabField: FinanceLabField,
        title: string,
    ) => {
        const metric = [...(metrics ?? []), ...(mfiMetrics ?? [])]?.find(
            (m) => m.info.derived.label === financeLabField,
        );

        setAIMContextView({
            type: AIMViewType.LABS_LINEAGE,
            metric: metric || null,
            title,
            isExpanded: true,
        });
    };

    const capitalProductValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_CAPITAL_PRODUCT,
    )?.values;
    const initialDisbursementValues = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_INITIAL_DISBURSEMENT,
    )?.values;
    const termValues = metrics?.find(
        (metric) => metric.info.derived.label === FinanceLabField.DEAL_TERM,
    )?.values;
    const currentPayPValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_CURRENT_PAY_P,
    )?.values;
    const repaymentFrequencyValues = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_REPAYMENT_FREQUENCY,
    )?.values;
    const currentPayTypeValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_CURRENT_PAY_TYPE,
    )?.values;

    const brokerFeeValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_BROKER_FEE,
    )?.values;

    const moicValues = metrics?.find(
        (metric) => metric.info.derived.label === FinanceLabField.DEAL_MOIC,
    )?.values;

    const irrValues = metrics?.find(
        (metric) => metric.info.derived.label === FinanceLabField.DEAL_IRR,
    )?.values;

    const monitoringFeeValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_MONITORING_FEE,
    )?.values;

    const prepaymentPenaltyValues = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_PREPAYMENT_PENALTY,
    )?.values;

    const legalFeesValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_LEGAL_FEES,
    )?.values;

    const recurringPaymentValues = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_RECURRING_PAYMENT,
    )?.values;

    const fundedDatedValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_FUNDED_DATED,
    )?.values;

    const collectionsValues = mfiMetrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_COLLECTIONS,
    )?.values;

    const disbursementsValues = mfiMetrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_DISBURSEMENTS,
    )?.values;

    const closeDateValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_CLOSE_DATE,
    )?.values;

    const cashYieldValues = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_CURRENT_PAY_CASH_P,
    )?.values;

    const covenantsSummaryValues = metrics?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.DEAL_COVENANTS_SUMMARY,
    )?.values;

    const capitalProductRaw = getLastValue(capitalProductValues);
    const capitalType =
        capitalProductRaw?.value ?? capitalProductRaw?.formattedValue ?? "-";
    const facilitySize = longFormMoney(data?.totalFacilitySize);
    const initialAdvance =
        getLastValue(initialDisbursementValues)?.formattedValue || "-";
    const termValue = getLastValue(termValues);
    const facilityTerm = termValue?.value
        ? `${termValue.value} months`
        : undefined;

    const productData: DetailTableRow[] = [
        {
            label: "Capital Type",
            value: capitalType,
            source: MetricSource.FINANCE_LAB,
            fieldName: FinanceLabField.DEAL_CAPITAL_PRODUCT,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_CAPITAL_PRODUCT,
                    "Capital Type",
                ),
        },
        {
            label: "Initial Advance",
            value: initialAdvance,
            source: MetricSource.FINANCE_LAB,
            fieldName: FinanceLabField.DEAL_INITIAL_DISBURSEMENT,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_INITIAL_DISBURSEMENT,
                    "Initial Advance",
                ),
        },
        {
            label: "Facility Size",
            value: facilitySize,
            source: DataSource.SFDC,
            fieldName: "sfdc.totalFacilitySize",
        },
        ...(showPreAlphaFields
            ? [
                  {
                      label: "Facility Term",
                      value: facilityTerm,
                      source: MetricSource.FINANCE_LAB,
                      fieldName: FinanceLabField.DEAL_TERM,
                      onClick: () =>
                          handleMetricClick(
                              FinanceLabField.DEAL_TERM,
                              "Facility Term",
                          ),
                  },
              ]
            : []),
    ];

    const interestRate = getLastValue(currentPayPValues)?.formattedValue || "-";
    const originationFee = longFormMoney(data?.originationFees);
    const moic = getLastValue(moicValues)?.formattedValue || "-";
    const cashYield = getLastValue(cashYieldValues)?.formattedValue || "-";
    const legalFees = getLastValue(legalFeesValues)?.formattedValue || "-";
    const feeType =
        getLastValue(currentPayTypeValues)?.formattedValue ||
        getLastValue(currentPayTypeValues)?.value ||
        "-";
    const brokerFee = getLastValue(brokerFeeValues)?.formattedValue || "-";
    const irr = getValueForDate(irrValues)?.formattedValue || "-";
    const monitoringFee =
        getLastValue(monitoringFeeValues)?.formattedValue || "-";
    const prepaymentPenalty =
        getLastValue(prepaymentPenaltyValues)?.formattedValue ||
        getLastValue(prepaymentPenaltyValues)?.value ||
        "-";

    const recurringPaymentRaw = getLastValue(recurringPaymentValues);
    const recurringPaymentValue =
        recurringPaymentRaw?.formattedValue ||
        recurringPaymentRaw?.value ||
        "-";
    const recurringPaymentDisplay =
        typeof recurringPaymentValue === "string" &&
        recurringPaymentValue.includes("(")
            ? recurringPaymentValue.split("(")[0].trim()
            : recurringPaymentValue;
    const monthlyPayment = recurringPaymentDisplay;
    const repaymentFrequencyRaw = getLastValue(repaymentFrequencyValues);
    const repaymentFrequencyLabel =
        repaymentFrequencyRaw?.value &&
        typeof repaymentFrequencyRaw.value === "string"
            ? FrequencyLabels[repaymentFrequencyRaw.value as Frequency]
            : undefined;
    const repaymentFrequency =
        repaymentFrequencyLabel ??
        repaymentFrequencyRaw?.formattedValue ??
        repaymentFrequencyRaw?.value ??
        "-";
    const targetCloseDate = data?.closeDate
        ? formatDate(data.closeDate, {
              hideTime: true,
              parseAsUTC: false,
              dateFormat: "YYYY-MM-DD",
          })
        : "-";
    const actualFundingDate =
        getLastValue(fundedDatedValues)?.formattedValue || "-";
    const collections = getLastValue(collectionsValues)?.formattedValue || "-";
    const actualCloseDate =
        getLastValue(closeDateValues)?.formattedValue || "-";
    const advances = getLastValue(disbursementsValues)?.formattedValue || "-";
    const useOfProceedsValues = metrics?.find(
        (metric) =>
            metric.info.derived.label === FinanceLabField.DEAL_USE_OF_PROCEEDS,
    )?.values;
    const useOfProceedsValue =
        getLastValue(useOfProceedsValues)?.formattedValue;
    const useOfProceedsFinLab = useOfProceedsValue
        ? USE_OF_FUNDS[useOfProceedsValue as UseOfFunds] || useOfProceedsValue
        : undefined;
    const useOfProceedsBackend =
        USE_OF_FUNDS[company?.capitalApplication.useOfFunds as UseOfFunds];

    console.log("useOfProceedsFinLab", useOfProceedsFinLab);
    console.log("useOfProceedsBackend", useOfProceedsBackend);

    const useOfProceeds = useOfProceedsFinLab || useOfProceedsBackend || "-";

    console.log("useOfProceeds", useOfProceeds);

    const covenants =
        getLastValue(covenantsSummaryValues)?.formattedValue ||
        getLastValue(covenantsSummaryValues)?.value ||
        "-";

    const economicsData: DetailTableRow[] = [
        ...(showPreAlphaFields
            ? [
                  {
                      label: "Fee Type",
                      value: feeType,
                      source: MetricSource.FINANCE_LAB,
                      fieldName: FinanceLabField.DEAL_CURRENT_PAY_TYPE,
                      onClick: () =>
                          handleMetricClick(
                              FinanceLabField.DEAL_CURRENT_PAY_TYPE,
                              "Fee Type",
                          ),
                  },
              ]
            : []),
        {
            label: "Referral Fee",
            value: brokerFee,
            source: MetricSource.FINANCE_LAB,
            fieldName: FinanceLabField.DEAL_BROKER_FEE,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_BROKER_FEE,
                    "Referral Fee",
                ),
        },
        ...(showPreAlphaFields
            ? [
                  {
                      label: "Interest Rate/Fee",
                      value: interestRate,
                      source: MetricSource.FINANCE_LAB,
                      fieldName: FinanceLabField.DEAL_CURRENT_PAY_P,
                      onClick: () =>
                          handleMetricClick(
                              FinanceLabField.DEAL_CURRENT_PAY_P,
                              "Interest Rate/Fee",
                          ),
                  },
                  {
                      label: "Prepayment Penalty",
                      value: prepaymentPenalty,
                      source: MetricSource.FINANCE_LAB,
                      fieldName: FinanceLabField.DEAL_PREPAYMENT_PENALTY,
                      onClick: () =>
                          handleMetricClick(
                              FinanceLabField.DEAL_PREPAYMENT_PENALTY,
                              "Prepayment Penalty",
                          ),
                  },
              ]
            : []),
        {
            label: "Origination Fee",
            value: originationFee,
            source: DataSource.SFDC,
            fieldName: "sfdc.originationFees",
        },
        {
            label: "Cash Yield",
            value: cashYield,
            source: MetricSource.FINANCE_LAB,
            fieldName: FinanceLabField.DEAL_CURRENT_PAY_CASH_P,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_CURRENT_PAY_CASH_P,
                    "Cash Yield",
                ),
        },
        ...(showPreAlphaFields
            ? [
                  {
                      label: "Monitoring Fee",
                      value: monitoringFee,
                      source: MetricSource.FINANCE_LAB,
                      fieldName: FinanceLabField.DEAL_MONITORING_FEE,
                      onClick: () =>
                          handleMetricClick(
                              FinanceLabField.DEAL_MONITORING_FEE,
                              "Monitoring Fee",
                          ),
                  },
                  {
                      label: "MOIC",
                      value: moic,
                      source: MetricSource.FINANCE_LAB,
                      fieldName: FinanceLabField.DEAL_MOIC,
                      onClick: () =>
                          handleMetricClick(FinanceLabField.DEAL_MOIC, "MOIC"),
                  },
                  {
                      label: "Legal Fees",
                      value: legalFees,
                      source: MetricSource.FINANCE_LAB,
                      fieldName: FinanceLabField.DEAL_LEGAL_FEES,
                      onClick: () =>
                          handleMetricClick(
                              FinanceLabField.DEAL_LEGAL_FEES,
                              "Legal Fees",
                          ),
                  },
                  {
                      label: "IRR",
                      value: irr,
                      source: MetricSource.FINANCE_LAB,
                      fieldName: FinanceLabField.DEAL_IRR,
                      onClick: () =>
                          handleMetricClick(FinanceLabField.DEAL_IRR, "IRR"),
                  },
              ]
            : []),
    ];

    const termsConditionsData: DetailTableRow[] = [
        {
            label: "Target Close Date",
            value: targetCloseDate,
            source: DataSource.SFDC,
            fieldName: "sfdc.closeDate",
        },
        {
            label: "Repayment Frequency",
            value: repaymentFrequency,
            source: MetricSource.FINANCE_LAB,
            fieldName: FinanceLabField.DEAL_REPAYMENT_FREQUENCY,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_REPAYMENT_FREQUENCY,
                    "Repayment Frequency",
                ),
        },
        {
            label: "Actual Close Date",
            value: actualCloseDate,
            source: MetricSource.FINANCE_LAB,
            fieldName: FinanceLabField.DEAL_CLOSE_DATE,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_CLOSE_DATE,
                    "Actual Close Date",
                ),
        },
        {
            label: "Total Advanced To Date",
            value: advances,
            source: MetricSource.FINANCE_LAB,
            fieldName: FinanceLabField.DEAL_DISBURSEMENTS,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_DISBURSEMENTS,
                    "Total Advanced To Date",
                ),
        },
        {
            label: "Actual Funding Date",
            value: actualFundingDate,
            source: MetricSource.FINANCE_LAB,
            fieldName: FinanceLabField.DEAL_FUNDED_DATED,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_FUNDED_DATED,
                    "Actual Funding Date",
                ),
        },
        ...(showComingSoon
            ? [
                  {
                      label: "Security",
                      value: COMING_SOON,
                  },
              ]
            : []),
        {
            label: "Collections To Date",
            value: collections,
            source: MetricSource.FINANCE_LAB,
            fieldName: FinanceLabField.DEAL_COLLECTIONS,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_COLLECTIONS,
                    "Collections To Date",
                ),
        },
        ...(showComingSoon
            ? [
                  {
                      label: "Security Features",
                      value: COMING_SOON,
                  },
              ]
            : []),
        {
            label: "Repayment Amount",
            value: monthlyPayment,
            source: MetricSource.FINANCE_LAB,
            fieldName: FinanceLabField.DEAL_RECURRING_PAYMENT,
            onClick: () =>
                handleMetricClick(
                    FinanceLabField.DEAL_RECURRING_PAYMENT,
                    "Repayment Amount",
                ),
        },
        {
            label: "Use of Proceeds",
            value: useOfProceeds,
            source: useOfProceedsFinLab
                ? MetricSource.FINANCE_LAB
                : DataSource.LEGACY_BACKEND,
            fieldName: "sfdc.useOfProceeds",
        },
    ];

    return {
        productData,
        economicsData,
        termsConditionsData,
        covenantsSummaryValues,
        covenants: {
            value: covenants,
            source: MetricSource.FINANCE_LAB,
            fieldName: FinanceLabField.DEAL_COVENANTS_SUMMARY,
        },
        isLoading:
            sfdcData.isLoading || isLoadingMetrics || isLoadingMfiMetrics,
        error: sfdcData.error,
    };
}
